/**
 * MOCKUP STANDALONE — Front Office "Look del Futuro"
 * Pagina isolata, non collegata al codice live. Solo per anteprima visiva.
 * Route: /mockup/fo (pubblica, no auth).
 */
import React from 'react';
import {
  Plus, Search, Bell, Sparkles, Zap, Droplets, Wind, AlertTriangle,
  Clock, Wrench, Camera, ChevronRight, Siren, ListChecks, Tv,
  CircleDot, ArrowUpRight, Activity, FileText, Filter
} from 'lucide-react';

// Palette dal design_guidelines.json — Enterprise Slate (cambia a runtime via theme switcher)
import { C } from './mockup/_tokens';

const fontHead = "'Inter', 'Geist', system-ui, sans-serif";  // approssimazione di Clash Display
const fontBody = "'Inter', system-ui, sans-serif";

export default function MockupFrontOffice() {
  return (
    <div style={{
      minHeight: '100vh', background: C.bg, color: C.text,
      fontFamily: fontBody,
      backgroundImage: `
        radial-gradient(ellipse 80% 50% at 50% -10%, rgba(212,175,55,0.08), transparent),
        radial-gradient(ellipse 60% 40% at 100% 100%, rgba(226,125,96,0.06), transparent)`,
    }}>
      {/* Watermark mockup label */}
      <div style={{
        position: 'fixed', top: 10, left: '50%', transform: 'translateX(-50%)',
        background: 'rgba(212,175,55,0.15)', color: C.gold,
        border: `1px solid ${C.gold}`,
        padding: '4px 14px', borderRadius: 999, fontSize: 11,
        letterSpacing: '0.2em', fontWeight: 600, zIndex: 100, backdropFilter: 'blur(12px)',
      }}>MOCKUP · LOOK DEL FUTURO · NON LIVE</div>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '60px 32px 32px' }}>
        {/* HEADER GLASS */}
        <Header />

        {/* HERO STATS (asymmetric, oversized numbers) */}
        <HeroStats />

        {/* MAIN: Live feed + Side rail */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 24, marginTop: 32 }}>
          <LiveTicketFeed />
          <SideRail />
        </div>

        {/* Floating FAB (mobile-style mock visible on desktop right) */}
        <FAB />

        {/* Footer note */}
        <div style={{ marginTop: 80, paddingTop: 24, borderTop: `1px solid ${C.border}`,
                      fontSize: 12, color: C.textSec, textAlign: 'center', letterSpacing: '0.1em' }}>
          HostFix · Front Office Dashboard · Concept v1 — Hospitality-coded, dark-mode dominant, gold accents
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- HEADER
const Header = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 24px', borderRadius: 18,
    background: C.surfGlass, border: `1px solid ${C.border}`, backdropFilter: 'blur(24px)',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 9,
          background: `linear-gradient(135deg, ${C.gold} 0%, #B8941F 100%)`,
          display: 'grid', placeItems: 'center',
          boxShadow: `0 0 24px ${C.goldGlow}`,
        }}>
          <Wrench size={16} color={C.bg} strokeWidth={2.5} />
        </div>
        <div style={{ fontFamily: fontHead, fontSize: 19, fontWeight: 600, letterSpacing: '-0.02em' }}>
          HostFix<span style={{ color: C.gold }}>.</span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <span style={{ color: C.textSec }}>Hotel</span>
        <div style={{
          padding: '6px 12px', background: C.surf, borderRadius: 8,
          border: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
        }}>
          <span style={{ fontWeight: 500 }}>Hotel Galleria · Milano</span>
          <ChevronRight size={13} color={C.textSec} style={{ transform: 'rotate(90deg)' }} />
        </div>
      </div>
    </div>

    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 14px', background: C.surf, borderRadius: 10,
        border: `1px solid ${C.border}`, minWidth: 280,
      }}>
        <Search size={14} color={C.textSec} />
        <span style={{ flex: 1, color: C.textSec, fontSize: 13 }}>Cerca ticket, camera, ospite...</span>
      </div>

      <button style={iconBtn}><Bell size={16} color={C.text} />
        <span style={{
          position: 'absolute', top: 7, right: 7, width: 7, height: 7,
          borderRadius: '50%', background: C.terra, boxShadow: `0 0 8px ${C.terra}`,
        }} /></button>

      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: `linear-gradient(135deg, ${C.terra}, ${C.gold})`,
        display: 'grid', placeItems: 'center', fontWeight: 600, fontSize: 13, color: C.bg,
        boxShadow: `0 0 18px ${C.goldGlow}`,
      }}>MR</div>
    </div>
  </div>
);

const iconBtn = {
  width: 38, height: 38, borderRadius: 10, background: C.surf,
  border: `1px solid ${C.border}`,
  display: 'grid', placeItems: 'center', cursor: 'pointer', position: 'relative',
};

// ---------------------------------------------------------------- HERO STATS
const HeroStats = () => (
  <div style={{ marginTop: 28, marginBottom: 8 }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 24 }}>
      <div>
        <div style={{ fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase',
                      color: C.textSec, fontWeight: 500, marginBottom: 6 }}>
          Buongiorno · Mercoledì 11 Giugno · 09:42
        </div>
        <h1 style={{
          fontFamily: fontHead, fontSize: 42, fontWeight: 600, margin: 0,
          letterSpacing: '-0.025em', lineHeight: 1.05,
        }}>
          Centrale operativa <span style={{ color: C.gold }}>Front Office</span>
        </h1>
        <div style={{ marginTop: 10, color: C.textSec, fontSize: 14, maxWidth: 620 }}>
          3 ticket urgenti richiedono attenzione. Il tempo medio di risposta oggi è di 14 min — sotto SLA del 22%.
        </div>
      </div>
    </div>

    <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr', gap: 16 }}>
      {/* Hero KPI: dominant */}
      <KpiCard
        label="Ticket urgenti aperti"
        value="3"
        sub="SLA 30 min · 2 in scadenza"
        accent={C.terra}
        prominent
        trend={[2, 4, 3, 5, 4, 3, 3]}
      />
      <KpiCard label="Aperti oggi" value="12" sub="+3 vs ieri" accent={C.gold} trend={[5, 8, 6, 9, 11, 10, 12]} />
      <KpiCard label="In lavorazione" value="07" sub="2 tecnici attivi" accent={C.cyan} trend={[4, 5, 7, 6, 8, 7, 7]} />
      <KpiCard label="Chiusi oggi" value="18" sub="Tempo medio 1h 24m" accent={C.emerald} trend={[10, 12, 14, 13, 16, 17, 18]} />
    </div>
  </div>
);

const KpiCard = ({ label, value, sub, accent, prominent, trend = [] }) => {
  const max = Math.max(...trend, 1);
  const W = 100, H = 28;
  const pts = trend.map((v, i) => `${(i / (trend.length - 1)) * W},${H - (v / max) * H}`).join(' ');
  return (
    <div style={{
      background: C.surf, borderRadius: 18, padding: prominent ? 26 : 22,
      border: `1px solid ${prominent ? accent : C.border}`,
      position: 'relative', overflow: 'hidden',
      boxShadow: prominent ? `0 0 40px ${accent}22 inset` : 'none',
    }}>
      {prominent && (
        <div style={{
          position: 'absolute', inset: 0,
          background: `radial-gradient(ellipse at top right, ${accent}22, transparent 60%)`,
          pointerEvents: 'none',
        }} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <span style={{
          fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: prominent ? accent : C.textSec, fontWeight: 600,
        }}>{label}</span>
        {prominent && <span style={{
          width: 8, height: 8, borderRadius: '50%', background: accent,
          boxShadow: `0 0 12px ${accent}`,
          animation: 'pulse 2s infinite',
        }} />}
      </div>
      <div style={{
        fontFamily: fontHead, fontSize: prominent ? 64 : 48, fontWeight: 600,
        letterSpacing: '-0.04em', lineHeight: 1, color: prominent ? accent : C.text,
      }}>{value}</div>
      <div style={{ color: C.textSec, fontSize: 12, marginTop: 10 }}>{sub}</div>
      {trend.length > 0 && (
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{ marginTop: 12, opacity: 0.7 }}>
          <polyline points={pts} fill="none" stroke={accent} strokeWidth="1.2" />
        </svg>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- LIVE FEED
const LiveTicketFeed = () => (
  <div style={{ background: C.surf, borderRadius: 22, border: `1px solid ${C.border}`, padding: 26 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
      <div>
        <div style={{ fontFamily: fontHead, fontSize: 22, fontWeight: 600, letterSpacing: '-0.02em' }}>
          Feed ticket live
        </div>
        <div style={{ fontSize: 12, color: C.textSec, marginTop: 4 }}>Aggiornato in tempo reale</div>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        {['Tutti', 'Urgenti', 'In lavoro', 'Chiusi'].map((p, i) => (
          <button key={p} style={{
            padding: '8px 14px', borderRadius: 999, fontSize: 12,
            border: `1px solid ${i === 1 ? C.terra : C.border}`,
            background: i === 1 ? `${C.terra}22` : 'transparent',
            color: i === 1 ? C.terra : C.textSec, cursor: 'pointer',
            fontWeight: i === 1 ? 600 : 400,
          }}>{p}</button>
        ))}
      </div>
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <TicketRow priority="urgent" room="208" cat="Idraulica" icon={Droplets}
        title="Perdita continua da scarico lavabo, allagamento parziale"
        time="2 min fa" tech="Luca M." photos={2} />
      <TicketRow priority="urgent" room="Lobby" cat="HVAC" icon={Wind}
        title="Aria condizionata centrale spenta da 14 min"
        time="14 min fa" tech="In attesa" photos={0} sla="-3m" />
      <TicketRow priority="high" room="412" cat="Elettrico" icon={Zap}
        title="Presa del lato letto fuori uso, ospite anziano"
        time="42 min fa" tech="Marco T." photos={1} />
      <TicketRow priority="medium" room="305" cat="Elettrodomestici" icon={Tv}
        title="TV: ingresso HDMI non rilevato dopo riavvio"
        time="1h 12m fa" tech="Sara K." photos={0} />
      <TicketRow priority="low" room="118" cat="Estetica" icon={ListChecks}
        title="Silicone giallo bordo doccia (verificato — basso impatto)"
        time="3h fa" tech="Da assegnare" photos={1} />
    </div>
  </div>
);

const TicketRow = ({ priority, room, cat, icon: Icon, title, time, tech, photos, sla }) => {
  const colorMap = { urgent: C.terra, high: '#F59E0B', medium: '#FBBF24', low: '#64748B' };
  const labelMap = { urgent: 'URGENTE', high: 'ALTA', medium: 'MEDIA', low: 'BASSA' };
  const col = colorMap[priority];
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '4px 1fr auto', gap: 16,
      padding: '14px 16px 14px 0', background: 'rgba(255,255,255,0.015)',
      borderRadius: 12, border: `1px solid ${C.border}`,
      transition: 'all 0.2s', cursor: 'pointer',
    }}>
      <div style={{ background: col, borderRadius: 12, boxShadow: priority === 'urgent' ? `0 0 16px ${col}` : 'none' }} />
      <div style={{ paddingLeft: 14, display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10, background: `${col}1a`,
          display: 'grid', placeItems: 'center',
        }}><Icon size={16} color={col} strokeWidth={2} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{
              padding: '2px 7px', borderRadius: 4, fontSize: 9.5, fontWeight: 700,
              letterSpacing: '0.1em', background: `${col}22`, color: col,
            }}>{labelMap[priority]}</span>
            <span style={{ fontFamily: fontHead, fontWeight: 600, fontSize: 14, color: C.text }}>
              {isNaN(room) ? room : `Cam ${room}`}
            </span>
            <span style={{ fontSize: 11, color: C.textSec }}>· {cat}</span>
            {sla && <span style={{
              fontSize: 10, color: C.terra, fontWeight: 600,
              padding: '2px 6px', background: `${C.terra}22`, borderRadius: 4,
            }}>SLA {sla}</span>}
          </div>
          <div style={{ fontSize: 13.5, color: C.text, opacity: 0.92, lineHeight: 1.35 }}>{title}</div>
          <div style={{ fontSize: 11, color: C.textSec, marginTop: 6, display: 'flex', gap: 14 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={11} /> {time}</span>
            <span>· {tech}</span>
            {photos > 0 && <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.gold }}>
              <Camera size={11} /> {photos}
            </span>}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', paddingRight: 12 }}>
        <ChevronRight size={16} color={C.textSec} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- SIDE RAIL
const SideRail = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
    {/* Storico camera problematiche */}
    <div style={{
      padding: 22, borderRadius: 20, background: C.surf,
      border: `1px solid ${C.terra}44`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
                       color: C.terra, fontWeight: 600 }}>Camere con storico critico</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[
          { r: '208', n: 3, c: 'Idraulica' },
          { r: '412', n: 2, c: 'Elettrica' },
          { r: '305', n: 2, c: 'HVAC' },
        ].map(c => (
          <div key={c.r} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
            <span style={{ fontWeight: 600 }}>Cam {c.r}</span>
            <span style={{ color: C.textSec }}>· {c.n} ticket negli ultimi 30 gg · {c.c}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Quick actions */}
    <div style={{ padding: 22, borderRadius: 20, background: C.surf, border: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
                    color: C.textSec, fontWeight: 600, marginBottom: 14 }}>Azioni rapide</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <QuickRow icon={Plus} label="Nuovo ticket" accent={C.gold} />
        <QuickRow icon={Filter} label="Filtri avanzati" />
        <QuickRow icon={Activity} label="Timeline turno" />
        <QuickRow icon={FileText} label="Guida apertura ticket" />
      </div>
    </div>

    {/* Emergency contacts */}
    <div style={{
      padding: 18, borderRadius: 18,
      background: `linear-gradient(135deg, ${C.terra}22, ${C.terra}08)`,
      border: `1px solid ${C.terra}55`,
      display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer',
    }}>
      <div style={{ width: 42, height: 42, borderRadius: 12,
                    background: `${C.terra}`, display: 'grid', placeItems: 'center',
                    boxShadow: `0 0 18px ${C.terra}88` }}>
        <Siren size={20} color="#fff" />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: '#fff' }}>Rubrica Emergenze</div>
        <div style={{ fontSize: 11, color: '#FECACA' }}>Accesso rapido · sempre disponibile</div>
      </div>
      <ChevronRight size={18} color={C.terra} />
    </div>
  </div>
);

const QuickRow = ({ icon: Icon, label, accent }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 12px', borderRadius: 10,
    background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}`,
    cursor: 'pointer',
  }}>
    <Icon size={15} color={accent || C.textSec} />
    <span style={{ flex: 1, fontSize: 13, color: accent || C.text }}>{label}</span>
    <ChevronRight size={12} color={C.textSec} />
  </div>
);

const primaryBtn = {
  background: `linear-gradient(135deg, ${C.gold}, #B8941F)`,
  color: C.bg, border: 'none', padding: '10px 18px',
  borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 6,
  boxShadow: `0 4px 24px ${C.goldGlow}`,
};
const secondaryBtn = {
  background: 'transparent', color: C.text,
  border: `1px solid ${C.border}`, padding: '10px 18px',
  borderRadius: 10, fontWeight: 500, fontSize: 13, cursor: 'pointer',
};

// ---------------------------------------------------------------- FAB
const FAB = () => (
  <button style={{
    position: 'fixed', bottom: 32, right: 32,
    width: 64, height: 64, borderRadius: '50%',
    background: `linear-gradient(135deg, ${C.gold}, #B8941F)`,
    border: 'none', cursor: 'pointer',
    boxShadow: `0 0 48px ${C.gold}88, 0 8px 24px rgba(0,0,0,0.4)`,
    display: 'grid', placeItems: 'center', zIndex: 50,
  }}>
    <Plus size={28} color={C.bg} strokeWidth={2.5} />
  </button>
);
